import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import {
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameMonth,
  isWeekend,
  isBefore,
  startOfDay,
  format,
} from 'date-fns';
import { ja } from 'date-fns/locale';
import { isHoliday } from '@holiday-jp/holiday_jp';
import { MyShift } from '../../../../domain/myShift';
import { DayCell } from './DayCell';

/**
 * 曜日ヘッダー
 */
const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土'];

/**
 * カレンダーグリッドのProps
 */
interface CalendarGridProps {
  /** 表示対象の月 */
  currentMonth: Date;
  /** 現在月のシフト希望一覧 */
  shifts: MyShift[];
  /**
   * 日付セルクリックハンドラー
   * @param date - クリックされた日付
   * @param shift - その日のシフト希望（存在する場合）
   */
  onDayClick: (date: Date, shift?: MyShift) => void;
}

/**
 * カレンダーグリッドコンポーネント
 */
export const CalendarGrid: React.FC<CalendarGridProps> = ({
  currentMonth,
  shifts,
  onDayClick,
}) => {
  const today = useMemo(() => startOfDay(new Date()), []);

  // カレンダーに表示する日付配列を生成
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart, { locale: ja });
    const calendarEnd = endOfWeek(monthEnd, { locale: ja });

    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentMonth]);

  // シフトデータをマップ化（日付文字列 -> シフト）
  const shiftMap = useMemo(() => {
    const map = new Map<string, MyShift>();
    shifts.forEach(shift => {
      map.set(shift.date, shift);
    });
    return map;
  }, [shifts]);

  // 日付の祝日判定
  const checkIsHoliday = (date: Date): boolean => {
    try {
      return isHoliday(date);
    } catch {
      return false;
    }
  };

  return (
    <Box sx={{ borderTop: '1px solid #e8e4df' }}>
      {/* 曜日ヘッダー */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gap: 0,
        }}
      >
        {WEEKDAY_LABELS.map((label, index) => (
          <Box
            key={label}
            sx={{
              p: 1,
              textAlign: 'center',
              backgroundColor: '#f8f7f5',
              borderRight: '1px solid #e8e4df',
              borderBottom: '1px solid #e8e4df',
            }}
          >
            <Typography
              variant="body2"
              fontWeight="bold"
              color={index === 0 ? '#ae686d' : index === 6 ? '#637d9e' : '#635d57'}
              sx={{ fontSize: 11 }}
            >
              {label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* カレンダーグリッド */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
          gap: 0,
        }}
      >
        {calendarDays.map(day => {
          const dateKey = format(day, 'yyyy-MM-dd');
          const shift = shiftMap.get(dateKey);
          const dayIsCurrentMonth = isSameMonth(day, currentMonth);
          const dayIsWeekend = isWeekend(day);
          const dayIsHoliday = checkIsHoliday(day);
          const dayIsPast = isBefore(day, today);

          return (
            <DayCell
              key={dateKey}
              date={day}
              isCurrentMonth={dayIsCurrentMonth}
              isWeekend={dayIsWeekend}
              isHoliday={dayIsHoliday}
              isPast={dayIsPast}
              shift={shift}
              onClick={() => onDayClick(day, shift)}
            />
          );
        })}
      </Box>
    </Box>
  );
};

export default CalendarGrid;
